// dashboard/src/helpers/audioDiagnostics.ts
//
// Framework-agnostic helpers for detecting silent audio capture, so both the
// user-facing MediaCheck tool and the production RecordScreen can refuse to
// upload dead files instead of discovering them server-side.
//
// The core insight behind this module: on managed Windows (and VDI, and when
// another app like Teams holds the mic), getUserMedia succeeds and MediaRecorder
// happily encodes a full-length file of pure digital silence. The recorder is
// not broken; the source track is silent. You cannot fix that in code — but you
// can measure it and tell the user, which is what these helpers are for.

const AudioCtx: typeof AudioContext =
  (window as any).AudioContext || (window as any).webkitAudioContext;

// ----- 1. Track state -------------------------------------------------------
// The cheapest, highest-signal diagnostic. `muted === true` (browser-controlled,
// distinct from `enabled`) means the OS/another app took the device — the
// classic corporate-Windows silent-capture signature.

export interface TrackDiagnostics {
  kind: string;
  label: string; // often reveals "Remote Audio", "Citrix HDX", "CABLE Output", etc.
  enabled: boolean;
  muted: boolean;
  readyState: MediaStreamTrackState;
  settings: MediaTrackSettings;
}

export const getTrackDiagnostics = (stream: MediaStream): TrackDiagnostics[] =>
  stream.getTracks().map((t) => ({
    kind: t.kind,
    label: t.label || '(no label)',
    enabled: t.enabled,
    muted: t.muted,
    readyState: t.readyState,
    settings: typeof t.getSettings === 'function' ? t.getSettings() : {},
  }));

// Wire this up when you start a stream so you catch a device being grabbed
// mid-recording. Returns a detach function.
export const watchAudioTrackMute = (
  stream: MediaStream,
  onChange: (event: 'mute' | 'unmute', track: MediaStreamTrack) => void
): (() => void) => {
  const detachers: Array<() => void> = [];
  stream.getAudioTracks().forEach((track) => {
    const onMute = () => onChange('mute', track);
    const onUnmute = () => onChange('unmute', track);
    track.addEventListener('mute', onMute);
    track.addEventListener('unmute', onUnmute);
    detachers.push(() => {
      track.removeEventListener('mute', onMute);
      track.removeEventListener('unmute', onUnmute);
    });
  });
  return () => detachers.forEach((d) => d());
};

// ----- 2. Live signal monitor ----------------------------------------------
// Runs an AnalyserNode over the live stream and tracks the loudest RMS seen.
// Call this for the whole duration a recording is active; if getMaxRms() is
// ~0 at the end, the mic produced nothing while recording.

export interface SignalMonitor {
  getMaxRms: () => number;
  getCurrentRms: () => number;
  stop: () => void;
}

export const createSignalMonitor = (stream: MediaStream): SignalMonitor => {
  const ctx = new AudioCtx();
  // iOS/Safari can hand back a suspended context; resume() is a no-op elsewhere.
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 1024;
  source.connect(analyser);

  const buf = new Uint8Array(analyser.fftSize);
  let maxRms = 0;
  let currentRms = 0;
  let raf: number | null = null;

  const tick = () => {
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    currentRms = Math.sqrt(sum / buf.length);
    if (currentRms > maxRms) maxRms = currentRms;
    raf = requestAnimationFrame(tick);
  };
  tick();

  return {
    getMaxRms: () => maxRms,
    getCurrentRms: () => currentRms,
    stop: () => {
      if (raf !== null) cancelAnimationFrame(raf);
      if (ctx.state !== 'closed') ctx.close().catch(() => {});
    },
  };
};

// ----- 3. Recorded-blob analysis -------------------------------------------
// Decode the finished blob and measure peak/RMS. A throw here is itself a
// signal (corrupt/undecodable). A successful decode with peak ~0 is the
// full-length-but-silent case you're seeing on the server.

export interface BlobSignal {
  durationSec: number;
  peak: number; // 0..1, loudest sample
  rms: number; // 0..1
  sampleRate: number;
  channels: number;
}

export const analyzeBlobSignal = async (blob: Blob): Promise<BlobSignal> => {
  const ctx = new AudioCtx();
  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
    let peak = 0;
    let sumSquares = 0;
    let count = 0;
    for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
      const data = audioBuffer.getChannelData(ch);
      for (let i = 0; i < data.length; i++) {
        const a = Math.abs(data[i]);
        if (a > peak) peak = a;
        sumSquares += data[i] * data[i];
        count++;
      }
    }
    return {
      durationSec: audioBuffer.duration,
      peak,
      rms: count > 0 ? Math.sqrt(sumSquares / count) : 0,
      sampleRate: audioBuffer.sampleRate,
      channels: audioBuffer.numberOfChannels,
    };
  } finally {
    if (ctx.state !== 'closed') await ctx.close();
  }
};

// ----- 4. Verdict -----------------------------------------------------------
// One place to decide silent vs quiet vs fine, so the tool and production agree.

export const SILENCE_PEAK_THRESHOLD = 0.005; // below this: treat as empty/silent
export const LOW_PEAK_THRESHOLD = 0.03; // below this: usably quiet, warn

export type SignalVerdict = 'ok' | 'quiet' | 'silent';

export const verdictFromPeak = (peak: number): SignalVerdict => {
  if (peak < SILENCE_PEAK_THRESHOLD) return 'silent';
  if (peak < LOW_PEAK_THRESHOLD) return 'quiet';
  return 'ok';
};

// ----- 5. getUserMedia failure classification -------------------------------
// getUserMedia rejects with a DOMException whose `name` tells us why. The one
// case `name` alone can't separate is an OS-level block (Windows "Microphone
// access" / "Let desktop apps access your microphone", macOS Privacy settings)
// from the user blocking the site: both are NotAllowedError. Chromium words the
// OS case "Permission denied by system"; elsewhere we fall back to the
// Permissions API, which reflects only the browser's site permission — so
// 'granted' plus NotAllowedError means something below the browser refused.

export type MediaErrorKind =
  | 'os-blocked'
  | 'browser-blocked'
  | 'in-use'
  | 'not-found'
  | 'insecure'
  | 'unsupported'
  | 'unknown';

export interface ClassifiedMediaError {
  kind: MediaErrorKind;
  message: string; // user-facing
  detail: string; // raw `name: message`, for logs/diagnostics
}

const queryMicPermission = async (): Promise<PermissionState | 'unknown'> => {
  try {
    const status = await navigator.permissions.query({ name: 'microphone' as PermissionName });
    return status.state;
  } catch {
    return 'unknown'; // older Firefox/Safari don't support 'microphone' here
  }
};

const osSettingsHint = (): string => {
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) {
    return "Open Windows Settings → Privacy & security → Microphone, turn on both 'Microphone access' and 'Let desktop apps access your microphone', then reload this page.";
  }
  if (/Macintosh|Mac OS X/i.test(ua)) {
    return 'Open System Settings → Privacy & Security → Microphone, allow your browser, then restart the browser and reload this page.';
  }
  return "Check your device's privacy settings to allow your browser to use the microphone, then reload this page.";
};

export const classifyMediaError = async (e: unknown): Promise<ClassifiedMediaError> => {
  const name = (e as any)?.name || '';
  const rawMessage = (e as any)?.message || String(e);
  const detail = `${name}: ${rawMessage}`;

  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError': {
      const perm = await queryMicPermission();
      if (/by system/i.test(rawMessage) || perm === 'granted') {
        return {
          kind: 'os-blocked',
          message: `Your computer's privacy settings are blocking microphone access. ${osSettingsHint()}`,
          detail,
        };
      }
      return {
        kind: 'browser-blocked',
        message:
          "Microphone access is blocked for this site. Click the lock or camera icon in your browser's address bar, allow the microphone, then reload this page.",
        detail,
      };
    }
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return {
        kind: 'in-use',
        message:
          'Your microphone could not be started. It may be in use by another application (such as Teams or Zoom). Close other apps that use the microphone, then reload this page.',
        detail,
      };
    case 'NotFoundError':
    case 'DevicesNotFoundError':
    case 'OverconstrainedError':
      return {
        kind: 'not-found',
        message: 'No microphone was found. Connect or enable a microphone, then reload this page.',
        detail,
      };
    case 'SecurityError':
      return {
        kind: 'insecure',
        message: 'Microphone access is not allowed on this page. Make sure you are using a secure (https) link.',
        detail,
      };
    case 'TypeError':
      return {
        kind: 'unsupported',
        message: 'Sorry, your browser does not support microphone access.',
        detail,
      };
    default:
      return {
        kind: 'unknown',
        message: `Could not open your microphone (${detail}).`,
        detail,
      };
  }
};

// Convenience: decode a blob and return a verdict, treating decode failure as
// silent (undecodable is, for your purposes, a failed capture).
export const isBlobSilent = async (blob: Blob): Promise<boolean> => {
  if (blob.size < 1024) return true;
  try {
    const { peak } = await analyzeBlobSignal(blob);
    return peak < SILENCE_PEAK_THRESHOLD;
  } catch {
    return true;
  }
};